import * as React from "react";
import { observer } from "mobx-react";
import { Message, MessageStates } from "sb-resp-ui";
import { Subheading, Mainheading } from "../../../typings";
import { getHeaderNames } from "../../../helpers";
import { BaseDict } from "../dict";
import { EventListPresenterState } from "../state";
import { RenderSubheading, RenderEvents, ToggleOddsBtnType, ViewAllLink, ViewAllBtn, RenderMainHeading, RenderFavorites, RenderCustomHeading, Placeholder, MiniPlaceholder } from "../components";
import "./EventListPresenter.less"
import { Event } from "../../../contracts";

@observer
export class EventListPresenter extends React.Component<{
    dataProvider: EventListPresenterState,
    dict: BaseDict,
    cms: any
},
EventListPresenterState> {
    dict: BaseDict;
    cms: any;
    renderedSubheadings: number;

    constructor(props) {
        super(props);

        this.dict = this.props.dict;
        this.cms = this.props.cms;
        this.state = this.props.dataProvider;
        this.renderedSubheadings = 0;
    }

    getSportFilterProps() {
        return {
            shouldShow: !this.state.isMiniEventCard && this.cms.ShowSortingFilter,
            handler: this.state.changeSubGrouping,
            subGroupBy: this.state.subGroupBy,
            dict: {
                League: this.dict.League,
                Time: this.dict.Time,
                SortBy: this.dict.SortBy
            },
            isDesktop: this.state.deviceDetect.isDesktop
        }
    }

    getDefaultHeadingProps(index: string, mainHeading: Mainheading, subHeading?: Subheading) {
        const isMain = !subHeading;
        const heading = subHeading || mainHeading;
        const eventFunction = isMain ? "displayEventByMainGrouping" : "displayEvents";

        return {
            key: `${isMain ? "main" : "sub"}-heading-${index}`,
            id: index.toString(),
            name: heading.name,
            cms: this.cms,
            dict: this.dict,
            eventsCountLength: this.state[eventFunction](heading.id).length,
            marketNames: this.prepareMarketNames(mainHeading, isMain),
            marketTemplates: this.state.getMarketTemplates(mainHeading.sportId),
            cardType: this.state.getSportCardType(mainHeading.sportId)
        }
    }

    getMainHeadingProps(mainHeadings: Mainheading[], index: number) {
        const heading = mainHeadings[index];
        return {
            ...this.getDefaultHeadingProps(index.toString(), heading),
            ... {
                subName: heading.subname,
                filterProps: this.getSportFilterProps(),
                shouldCollapse: (mainHeadings.length > 1),
                toggleOddsBtn: this.renderToggleOddsBtn(index)
            }
        }
    }

    getSubHeadingProps(mainHeading: Mainheading, subheading: Subheading) {
        const index = this.state.generateSubheadingId(mainHeading.id, subheading.id);
        return {
            ...this.getDefaultHeadingProps(index, mainHeading, subheading),
            ... {
                renderedSubheadings: this.renderedSubheadings++
            }
        }
    }

    showPlaceholder() {
        return this.state.isMiniEventCard ?
            <MiniPlaceholder eventsCount={this.cms.EventsCount} /> :
            <Placeholder eventsCount={this.cms.EventsCount} />
    }

    showMessage() {
        return this.state.isLoading ? this.showPlaceholder() :
            <Message key="no-events" text={this.dict.NoEvents} type={MessageStates.info} />
    }

    prepareMarketNames(mainHeading: Mainheading, isMainHeading: boolean) {
        const configuration = this.state.options.dataProvider.cardConfiguration[mainHeading.sportId]
            || this.state.options.dataProvider.cardConfiguration[0];
        const shouldShowNames = this.state.shouldShowNamesInHeading(mainHeading, isMainHeading);

        return shouldShowNames
            ? getHeaderNames(configuration, this.dict, mainHeading.sportId, isMainHeading)
            : [];
    }

    renderViewAllLive() {
        return <ViewAllBtn onClick={this.state.openViewAllLiveLink.bind(this.state)} text={this.dict.ViewAllBtn} linkUrl={this.state.getViewAllLiveLink()} />
    }

    renderEvents(index: number, events: Event[], mainHeading: Mainheading, chunks: number[], subHeading?: Subheading) {

        const id = subHeading ? subHeading.id : mainHeading.id;
        return <>
            {!subHeading && this.state.shouldShowCustomSubheading(mainHeading) && this.renderCustomSubheading(mainHeading)}
            <RenderEvents
                key={`events-content-${index}`}
                state={this.state}
                events={events}
                cmsSettings={{
                    ExpandedEvents: this.cms.ExpandedEvents,
                    ShowTeamNameML: this.cms.ShowTeamNameML,
                    ShowViewAllLive: this.cms.ShowViewAllLive,
                    NumberOfSelectionsToShow: this.cms.NumberOfSelectionsToShow
                }}
                eventChunkHeights={chunks}
                dict={this.dict}>
                {<ViewAllLink
                    key={`view-all-${id}`}
                    title={this.dict.ViewAllLink}
                    handler={this.state.openLeagueTranspiler.bind(this.state, mainHeading.sportId, id)}
                    href={(subHeading && subHeading.linkUrl) || ""}
                />}
            </RenderEvents>
        </>
    }

    renderFavoritesSection() {
        return <RenderFavorites
            name={this.dict.Favorites}
            cms={this.props.cms}
            state={this.state}
            getMarketNames={this.prepareMarketNames.bind(this)}
            getEvents={this.renderEvents.bind(this)}
        />
    }

    renderCustomSubheading(mainHeading: Mainheading) {
        const cardType = this.state.getSportCardType(mainHeading.sportId);
        const marketTemplates = [];

        return <RenderCustomHeading id={mainHeading.id} marketNames={this.prepareMarketNames(mainHeading, false)} marketTemplates={marketTemplates} cardType={cardType} />
    }

    renderToggleOddsBtn(index: number): ToggleOddsBtnType {
        return {
            shouldShow: index === 0 && this.state.isMiniEventCard && !this.state.showFavoritesSection,
            toggled: this.state.showOddBtns,
            tooltipMessage: this.state.tooltipMessage,
            onClick: this.state.toggleOddsBtnOnClick
        }
    }

    renderEventsContent(mainHeading: Mainheading, mainIndex: number) {
        if (this.state.shouldHideSubheadings) {
            let displayEvents = this.state.displayEvents(mainHeading.id);
            let eventChunkHeights = this.state.getEventsHeight(displayEvents);
            return this.renderEvents(mainIndex, displayEvents, mainHeading, eventChunkHeights)
        } else {
            return this.state.displaySubheadings(mainHeading.id).map(
                (subheading: Subheading, subIndex: number) => {

                    let eventChunkHeights = this.state.getEventsHeight(subheading.displayEvents);
                    return <RenderSubheading {...this.getSubHeadingProps(mainHeading, subheading)}>
                            {this.state.showFavoritesSection && subheading.displayEvents.length === 0
                                ? <Message key="all-events-in-fav" text={this.dict.AllEventsInFavorites} type={MessageStates.info} />
                                : this.renderEvents(subIndex, subheading.displayEvents, mainHeading, eventChunkHeights, subheading)
                            }
                        </RenderSubheading>
                })
        }
    }

    render() {
        this.renderedSubheadings = 0;
        return (
            <>
                {!this.state.hasLoadEvents
                    ? this.showMessage()
                    : <>
                        {this.state.showFavoritesSection && this.renderFavoritesSection()}
                        {this.state.displayMainHeadings.map(
                            (mainHeading: Mainheading, mainIndex: number, mainHeadings: Mainheading[]) => {
                                if (!this.state.displayEventByMainGrouping(mainHeading.id).length) { return; }

                                return <RenderMainHeading {...this.getMainHeadingProps(mainHeadings, mainIndex)} marketTemplates={this.state.getMarketTemplates(mainHeading.sportId)}>
                                    {this.renderEventsContent(mainHeading, mainIndex)}
                                </RenderMainHeading>
                            })
                        }
                        {this.cms.ShowViewAllLive && this.renderViewAllLive()}
                    </>
                }
            </>
        )
    }
}